<template>
  <div class="grid grid-cols-1 lg:grid-cols-2 col-gap-12 row-gap-11 items-start">

    <BaseInput v-model.trim="$v.name.$model" class="lg:order-1"
               :label="$t('nameLabel')"
               :class="{'--error':$v.name.$error }">
      <template v-slot:errors="">
        <div class="error" v-if="!$v.name.required">{{ $t('errors.required') }}</div>
        <div class="error" v-else-if="!$v.name.noSpecSymbols">{{ $t('errors.noSpecSymbols') }}</div>
      </template>
    </BaseInput>
    <BaseSelect v-model="currency" class="lg:order-2"
                :options="currenciesList"
                :label="`name_${$i18n.locale}`"
                :select-label="$t('currencyLabel')"
                track-by="id"
                :group-select="false"
                :searchable="true"
                :show-labels="false"
                :allow-empty="false"/>
    <div class="relative lg:order-3" :class="{'-mb-6':!broker}">
      <BaseSelect v-model="broker"
                  :options="brokersList"
                  class="flex-1"
                  :class="{'--error':$v.broker.$error}"
                  label="name"
                  :select-label="$t('brokerLabel')"
                  track-by="id"
                  :group-select="false"
                  :searchable="false"
                  :show-labels="false"
                  :allow-empty="false">
        <template v-slot:errors>
          <div class="error" v-if="$v.broker.$invalid">{{ $t('errors.required') }}</div>
        </template>
      </BaseSelect>
      <div v-if="!broker" @click="toggleNoBrokerTip=!toggleNoBrokerTip"
           class="inline-block ml-6 mt-4
            text-text-ternary text-2sm border-b border-dashed border-border
            hover:border-red hover:text-red cursor-pointer transition duration-200">
        {{ $t('labelNoBroker') }}
      </div>
    </div>
    <AlertBlock v-if="!broker && toggleNoBrokerTip" class="lg:col-span-2 lg:order-5"
                v-html="$t('tipNoBroker')"/>
    <AlertBlock v-if="!!broker && !broker.class_name" class="lg:col-span-2 lg:order-5 -mt-4"
                v-html="$t('tipNoImportBroker')"/>
    <PercentInput v-model="commission" class="lg:order-4"
                  :decimals="4"
                  :label="$t('commissionLabel')"
                  placeholder="0.00 %"/>
    <BaseTextarea v-model="note" :label="$t('noteLabel')" class="lg:col-span-2 lg:order-6"/>
    <div v-if="!edit_portfolio" class="grid grid-cols-2 items-center col-gap-12 lg:order-7">
      <BaseButton theme="red"
                  class="w-78 lg:w-91"
                  :loading="submitted"
                  @click="add">
        {{ $t('createPortfolio') }}
      </BaseButton>
    </div>
    <div v-else
         class="grid grid-cols-2 row-gap-8 col-gap-8
                lg:col-span-2 lg:flex flex-wrap items-center lg:row-gap-0 lg:col-gap-0 lg:order-7">
      <BaseButton @click="save"
                  :loading="submitted"
                  theme="gray"
                  class="w-78 lg:w-91 mr-8">
        {{ $t('saveChanges') }}
      </BaseButton>
      <BaseButton @click="$emit('cancelEdit')"
                  :loading="submitted"
                  class="w-50 lg:w-68 mr-auto"
                  theme="white">
        {{ $t('cancel') }}
      </BaseButton>
      <ArchiveBtn v-if="edit_portfolio"
                  class="mr-8"
                  :submitted="submitted"
                  revert-is-impossible
                  @confirm="archive">
        <template v-slot:text>
          {{ $t('removeLabel') }}
        </template>
      </ArchiveBtn>
      <ArchiveBtn v-if="edit_portfolio"
                  :submitted="submitted"
                  @confirm="purge">
        <template v-slot:icon>
          <font-awesome-icon icon="eraser" class="text-md"/>
        </template>
        <template v-slot:text>{{ $t('purgeLabel') }}</template>
      </ArchiveBtn>
    </div>
  </div>
</template>

<script>
import {helpers, required} from 'vuelidate/lib/validators'
import {mapActions, mapGetters, mapState} from "vuex";
import BaseButton from "@/components/UI/BaseButton";
import BaseInput from "@/components/UI/BaseInput/BaseInput";
import BaseSelect from "@/components/UI/BaseSelect/BaseSelect";
import PercentInput from "@/components/UI/BaseInput/PercentInput";
import BaseTextarea from "@/components/UI/BaseTextarea";
import ArchiveBtn from "@/components/UI/ArchiveBtn";
import AlertBlock from "@/components/UI/AlertBlock";

const noSpecSymbols = helpers.regex('noSpecSymbols', /^[^*|\/":<>[\]{}`\\()';@&!$]+$/)

export default {
  name: "InvestmentsSettingsForm",
  components: {
    AlertBlock,
    ArchiveBtn,
    BaseTextarea,
    PercentInput,
    BaseSelect,
    BaseInput,
    BaseButton,
  },
  props: {
    edit_portfolio: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      submitted: false,
      name: '',
      currency: null,
      broker: null,
      commission: null,
      note: '',
      currenciesList: [
        {
          id: 123,
          code: "RUB",
          symbol: "₽",
          name_en: "Russian Ruble",
          name_ru: "Российский рубль",
          is_base: 1,
          is_relative: 1,
          decimals: 2,
          is_active: 1
        },
        {
          id: 150,
          code: "USD",
          symbol: "$",
          name_en: "United States Dollar",
          name_ru: "Доллар США",
          is_base: 1,
          is_relative: 1,
          decimals: 2,
          is_active: 1
        },
        {
          id: 47,
          code: "EUR",
          symbol: "€",
          name_en: "Euro",
          name_ru: "Евро",
          is_base: null,
          is_relative: null,
          decimals: 2,
          is_active: 1
        }],
      toggleNoBrokerTip: false
    }
  },
  computed: {
    ...mapState('space', ['current_space']),
    ...mapGetters('investments/brokers', ['brokersList']),
  },
  validations: {
    name: {
      required,
      noSpecSymbols
    },
    broker: {
      required
    }
  },
  methods: {
    ...mapActions('investments/portfolios', ['createPortfolio', 'getPortfolios', 'updatePortfolio', 'archivePortfolio', 'purgePortfolio']),
    clearFields() {
      this.name = '';
      this.commission = null;
      this.note = '';
      this.$v.$reset();
    },
    setFields(item) {
      this.name = item.name;
      this.commission = item.commission;
      this.note = item.note;
      this.currency = this.currenciesList.filter(i => item.currency === i.code)[0]
      this.broker = this.brokersList.filter(i => item.broker_id === i.id)[0]
    },
    add() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.submitted = true;
        let data = {
          space_id: this.current_space.data.id,
          name: this.name,
          currency: this.currency.code,
          broker_id: this.broker.id,
          commission: this.commission,
          note: this.note
        }
        this.createPortfolio(data).then((res) => {
          if (res.body.success) {
            this.getPortfolios();
            this.clearFields();
            this.$toasted.success('Портфель создан')
          } else {
            console.log(res);
          }
        }).finally(() => {
          this.submitted = false;
        });
      } else {
        this.$toasted.error('Проверьте поля')
      }
    },
    save() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.submitted = true;
        let data = {
          id: this.edit_portfolio.id,
          name: this.name,
          currency: this.currency.code,
          broker_id: this.broker.id,
          commission: this.commission,
          note: this.note
        }
        this.updatePortfolio(data).then((res) => {
          if (res.body.success) {
            this.$emit('cancelEdit');
            this.getPortfolios();
            this.$toasted.success('Портфель изменен')
          } else {
            alert(res);
          }
        }).finally(() => {
          this.submitted = false;
        });
      } else {
        this.$toasted.error('Проверьте поля')
      }
    },
    archive() {
      this.submitted = true;
      this.archivePortfolio(this.edit_portfolio.id).then(res => {
        if (res.body.success) {
          this.$emit('cancelEdit');
          this.getPortfolios();
          this.$toasted.info('Портфель удален')
        } else {
          console.log(res)
        }
      }).catch((err) => {
        console.log(err)
        this.$toasted.error('Произошла ошибка')
      }).finally(() => {
        this.submitted = false;
      })
    },
    purge() {
      this.submitted = true;
      this.purgePortfolio(this.edit_portfolio.id).then(res => {
        if (res.body.success) {
          this.$emit('cancelEdit');
          this.getPortfolios();
          this.$toasted.info('Портфель очищен')
        } else {
          console.log(res)
        }
      }).catch((err) => {
        console.log(err)
        this.$toasted.error('Произошла ошибка')
      }).finally(() => {
        this.submitted = false;
      })
    }
  },
  created() {
    if (!this.edit_portfolio) {
      this.currency = this.currenciesList.filter(i => i.id === this.current_space.data.currency_id)[0];
    }
  },
  watch: {
    edit_portfolio: {
      immediate: true,
      handler: function (newVal) {
        if (!!newVal) {
          this.setFields(newVal);
        }
      }
    },
    broker() {
      this.toggleNoBrokerTip = false;
    }
  },
  i18n: {
    messages: {
      en: {
        formTitle: 'Create invest portfolio',
        nameLabel: 'Portfolio name',
        currencyLabel: 'Currency',
        brokerLabel: 'Broker',
        commissionLabel: 'Fixed commission in %',
        noteLabel: 'Note',
        createPortfolio: 'Create portfolio',
        saveChanges: 'Save changes',
        cancel: 'Cancel',
        removeLabel: 'Delete portfolio',
        purgeLabel: 'Clear portfolio',
        labelNoBroker: 'Can\'t find your broker in list?',
        tipNoBroker: 'We are ready to quickly integrate your broker into MoneyKeeper.' +
            '<br>' +
            'To do this, email to ' +
            '<a class="text-red border-b border-b-red hover:text-red-hover hover:border-b-red-hover transition duration-200" href="mailto:support@moneykeeper.com">support@moneykeeper.com</a>  ' +
            'indicating the name of the broker, and it is highly desirable to attach a broker report file.' +
            '<br>' +
            'If you do not know how to form it, write to the support service of your broker. File format - preferably XLS, CSV.' +
            '<br>' +
            'We will quickly implement the import option and you will be able to upload your reports in the coming days.',
        tipNoImportBroker: 'We have not yet implemented the function of downloading reports for this broker, but we really want to.' +
            '<br>' +
            'Email to ' +
            '<a class="text-red border-b border-b-red hover:text-red-hover hover:border-b-red-hover transition duration-200" href="mailto:support@moneykeeper.com">support@moneykeeper.com</a> ' +
            'with the name of the broker and attach the broker report file.' +
            '<br>' +
            'If you do not know how to form it, write to the support service of your broker. File format - preferably XLS, CSV.' +
            '<br>' +
            'We will quickly implement the import option and you will be able to upload your reports in the coming days.',
      },
      ru: {
        formTitle: 'Добавление инвестиционного портфеля',
        nameLabel: 'Название портфеля',
        currencyLabel: 'Валюта',
        brokerLabel: 'Брокер',
        commissionLabel: 'Фиксированная комиссия в %',
        noteLabel: 'Заметка',
        createPortfolio: 'Создать портфель',
        saveChanges: 'Сохранить изменения',
        cancel: 'Отмена',
        removeLabel: 'Удалить портфель',
        purgeLabel: 'Очистить портфель',
        labelNoBroker: 'Вашего брокера нет в списке?',
        tipNoBroker: 'Мы готовы оперативно интегрировать вашего брокера в MoneyKeeper.' +
            '<br>' +
            'Для этого напишите на <a class="text-red border-b border-b-red hover:text-red-hover hover:border-b-red-hover transition duration-200" href="mailto:support@moneykeeper.com">support@moneykeeper.com</a>  с указанием названия брокера и крайне желательно - приложив файл брокерского отчета.' +
            '<br>' +
            'Если вы не знаете, как его сформировать, напишите в службу поддержки вашего брокера. Формат файлов - желательно XLS, CSV.' +
            '<br>' +
            'Мы оперативно реализуем возможность импорта и вы сможете загрузить свои отчеты в ближайшие дни.',
        tipNoImportBroker: 'Мы пока не реализовали функцию загрузки отчетов для этого брокера, однако очень хотим.' +
            '<br>' +
            'Напишите на ' +
            '<a class="text-red border-b border-b-red hover:text-red-hover hover:border-b-red-hover transition duration-200" href="mailto:support@moneykeeper.com">support@moneykeeper.com</a> ' +
            'с указанием названия брокера и приложите файл брокерского отчета.' +
            '<br>' +
            'Если вы не знаете, как его получить, напишите в службу поддержки брокера. Формат файлов - желательно XLS, CSV.' +
            '<br>' +
            'Мы оперативно реализуем возможность импорта и вы сможете загрузить свои отчеты в ближайшие дни.',
      }
    }
  }
}
</script>
